import { Link } from "react-router-dom";
import { Reveal } from "@/hooks/useReveal";
import { ArrowUpRight } from "lucide-react";

/**
 * Three-tile editorial promo row that sits immediately below the hero.
 * Inspired by Livel Real Estate's homepage tile composition.
 *
 *  Tile 1 — Why Manzano (internal route)
 *  Tile 2 — What is your home worth? (external new-tab URL)
 *  Tile 3 — Featured Listings (anchor scroll)
 */

const TILES = [
  {
    id: "why",
    overline: "Strategy",
    label: "Why Manzano",
    href: "/why-manzano",
    type: "internal",
    image:
      "https://images.unsplash.com/photo-1518837695005-2083093ee35b?auto=format&fit=crop&w=900&q=80",
    grayscale: true,
  },
  {
    id: "search",
    overline: "Property Search",
    label: "Search Homes",
    href: "https://www.manzanorealestategroup.com/fine/real/estate/newsearch",
    type: "external",
    image: "/assets/tiles/property-search.jpg",
    grayscale: false,
  },
  {
    id: "valuation",
    overline: "Valuation",
    label: "What Is Your Home Worth?",
    href: "https://www.manzanorealestategroup.com/fine/real/estate/marketeval",
    type: "external",
    image:
      "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=900&q=80",
    grayscale: false,
  },
  {
    id: "featured",
    overline: "Portfolio",
    label: "Featured Listings",
    href: "#listings",
    type: "anchor",
    image:
      "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=900&q=80",
    grayscale: false,
  },
];

const TileInner = ({ tile }) => (
  <>
    <img
      src={tile.image}
      alt={tile.label}
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onError={(e) => {
        e.currentTarget.style.display = "none";
      }}
      className={`absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105 ${
        tile.grayscale ? "grayscale" : ""
      }`}
    />
    {/* Overlay for legibility */}
    <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/70 transition-opacity duration-700 group-hover:from-black/40 group-hover:to-black/80" />
    {/* Top overline */}
    <span className="absolute top-5 left-5 md:top-6 md:left-6 text-[9px] tracking-[0.32em] uppercase text-white/70 z-10">
      {tile.overline}
    </span>
    {/* Arrow */}
    <span className="absolute top-5 right-5 md:top-6 md:right-6 text-white/70 group-hover:text-white transition-all duration-500 group-hover:-translate-y-1 group-hover:translate-x-1 z-10">
      <ArrowUpRight size={16} strokeWidth={1.25} />
    </span>
    {/* Bottom label */}
    <div className="absolute inset-x-0 bottom-0 p-5 md:p-6 z-10">
      <h3 className="font-heading text-white text-xl sm:text-2xl md:text-[1.6rem] lg:text-[1.85rem] leading-[1.1] tracking-tight">
        {tile.label}
      </h3>
      <div className="mt-4 h-px w-8 bg-white/60 group-hover:w-16 transition-all duration-700" />
    </div>
  </>
);

const HeroTiles = () => {
  return (
    <section
      data-testid="hero-tiles-section"
      className="relative bg-white pt-16 md:pt-24 px-6 md:px-12"
      aria-label="Quick links"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {TILES.map((tile, i) => {
          const common =
            "group relative block aspect-[3/4] md:aspect-[2/3] overflow-hidden bg-[#0a0a0a] focus:outline-none focus:ring-2 focus:ring-white/40";

          if (tile.type === "internal") {
            return (
              <Reveal key={tile.id} delay={i * 120}>
                <Link
                  to={tile.href}
                  data-testid={`hero-tile-${tile.id}`}
                  className={common}
                >
                  <TileInner tile={tile} />
                </Link>
              </Reveal>
            );
          }
          if (tile.type === "external") {
            return (
              <Reveal key={tile.id} delay={i * 120}>
                <a
                  href={tile.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`hero-tile-${tile.id}`}
                  className={common}
                >
                  <TileInner tile={tile} />
                </a>
              </Reveal>
            );
          }
          // anchor — smooth scroll
          return (
            <Reveal key={tile.id} delay={i * 120}>
              <a
                href={tile.href}
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById(tile.href.replace("#", ""));
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                data-testid={`hero-tile-${tile.id}`}
                className={common}
              >
                <TileInner tile={tile} />
              </a>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};

export default HeroTiles;
